import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Show, useUser } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, Clock, FileText, Gavel, MapPin, ShieldCheck, Weight, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Countdown, useCountdown } from "@/components/Countdown";
import { CATEGORY_MAP, MODE_MAP } from "@/lib/data";
import { API, clp, errMsg, fileUrl, lotImage, num, useApi } from "@/lib/api";
import { DealBar } from "@/components/DealBar";
import { SinaderPanel } from "@/components/SinaderPanel";

const BidForm = ({ data, reload }) => {
  const api = useApi();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const left = useCountdown(data.lot.auction_ends_at);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("post", `/lots/${data.lot.id}/bids`, { amount: Number(amount) });
      toast.success("¡Oferta registrada! Vas liderando.");
      setAmount("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (!left) return <p className="text-sm text-slate-400" data-testid="bid-closed">La subasta finalizó.</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="bid-form">
      <Input type="number" min={data.min_next_bid} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Mínimo ${clp(data.min_next_bid)}`}
        className="h-12 bg-[#122a21] border-[#1b3d30] text-white font-mono" data-testid="bid-amount-input" required />
      <Button type="submit" disabled={busy} className="w-full h-12 rounded-full font-semibold hover:bg-[#00d890]" data-testid="lot-card-bidding-btn">
        <Gavel className="w-4 h-4" /> {busy ? "Enviando..." : "Ofertar ahora"}
      </Button>
    </form>
  );
};

const AuctionPanel = ({ data, reload }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const isOwner = user?.id === data.lot.owner_id;
  const { lot, bids } = data;
  return (
    <div className="bg-[#0a1f18] rounded-2xl p-6 text-white space-y-5 border border-[#1b3d30]" data-testid="auction-panel">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-400"><span className="live-dot w-2 h-2 rounded-full bg-red-500" /> Subasta en vivo</span>
        <Countdown endsAt={lot.auction_ends_at} className="text-lg text-[#00f2a1]" testId="auction-countdown" />
      </div>
      <div>
        <p className="text-xs text-slate-400">{lot.current_bid ? "Oferta más alta" : "Precio base"}</p>
        <p className="font-display text-4xl font-bold" data-testid="auction-current-bid">{clp(lot.current_bid || lot.starting_bid)}</p>
        <p className="text-xs text-slate-400 mt-1" data-testid="auction-bid-count">{lot.bid_count} {lot.bid_count === 1 ? "oferta" : "ofertas"}</p>
      </div>
      <Show when="signed-in">
        {isOwner ? <p className="text-sm text-slate-400" data-testid="bid-owner-note">Es tu lote: aquí verás las ofertas en tiempo real.</p> : <BidForm data={data} reload={reload} />}
      </Show>
      <Show when="signed-out">
        <Button onClick={() => navigate("/sign-in")} className="w-full h-12 rounded-full font-semibold" data-testid="bid-login-btn">Inicia sesión para ofertar</Button>
      </Show>
      {bids.length > 0 && (
        <ul className="border-t border-[#1b3d30] pt-4 space-y-2 text-sm" data-testid="bid-history">
          {bids.slice(0, 6).map((b, i) => (
            <li key={i} className="flex justify-between text-slate-300"><span>{b.bidder}</span><span className="font-mono">{clp(b.amount)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FixedPanel = ({ lot }) => (
  <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-3" data-testid="fixed-panel">
    <p className="text-xs text-slate-500">{MODE_MAP[lot.mode].label}</p>
    <p className="font-display text-4xl font-bold text-[#05130e]" data-testid="lot-price">{lot.mode === "donacion" ? "Sin costo" : clp(lot.price)}</p>
    <p className="text-sm text-slate-600">{lot.mode === "donacion" ? "Prioridad para liceos técnicos, artesanos y emprendedores de la economía popular." : "Precio total del lote, retiro a cargo del comprador."}</p>
  </div>
);

export default function LotDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [img, setImg] = useState(0);

  const load = useCallback(() => {
    axios.get(`${API}/lots/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <main className="max-w-3xl mx-auto py-24 text-center" data-testid="lot-not-found"><p>Lote no encontrado.</p><Link to="/marketplace" className="text-[#00915f]">Volver al marketplace</Link></main>;
  if (!data) return <main className="max-w-[1200px] mx-auto px-5 py-12"><Skeleton className="h-[480px] rounded-2xl" /></main>;
  const { lot, certificates } = data;
  const images = lot.photos.length ? lot.photos.map(fileUrl) : [lotImage(lot)];

  return (
    <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-10" data-testid="lot-detail-page">
      <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#05130e] mb-6" data-testid="lot-back-link"><ArrowLeft className="w-4 h-4" /> Marketplace</Link>
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-4">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100"><img src={images[img]} alt={lot.title} className="w-full h-full object-cover" data-testid="lot-main-image" /></div>
          {images.length > 1 && (
            <div className="flex gap-3">{images.map((src, i) => (
              <button key={src} onClick={() => setImg(i)} className={`w-20 h-16 rounded-lg overflow-hidden border-2 ${i === img ? "border-[#00f2a1]" : "border-transparent"}`} data-testid={`lot-thumb-${i}`}><img src={src} alt="" className="w-full h-full object-cover" /></button>
            ))}</div>
          )}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h2 className="font-semibold text-lg">Ficha técnica</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              {[["Familia", CATEGORY_MAP[lot.category]?.name], ["Código LER", lot.ler_code ? `${lot.ler_code} · ${lot.ler_label}` : "—"], ["Material", lot.material_tag], ["Estado físico", lot.physical_state || "—"], ["Peso exacto", `${num(lot.weight_tons, 3)} t${lot.weighed_calibrated ? " (báscula calibrada)" : ""}`], ["Generador", lot.generator ? `${lot.generator.razon_social} · RUT ${lot.generator.rut} · VU ${lot.generator.vu_code}` : lot.owner_name]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-slate-500">{k}</dt><dd className="font-medium text-[#05130e]">{v}</dd></div>
              ))}
            </dl>
            {lot.description && <p className="text-sm text-slate-600 leading-relaxed">{lot.description}</p>}
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#0a1f18] text-[#00f2a1]">{lot.material_tag}</span>
              {lot.verified && <span className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#00f2a1]/15 text-[#007a50] border border-[#00f2a1]/40" data-testid="lot-verified-badge"><ShieldCheck className="w-3.5 h-3.5" /> Pureza verificada</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05130e]" data-testid="lot-title">{lot.title}</h1>
            <div className="space-y-2 text-sm text-slate-600">
              <p className="flex items-center gap-2"><Weight className="w-4 h-4" /> {num(lot.weight_tons, 2)} Toneladas disponibles</p>
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {lot.comuna}, {lot.region}</p>
              {lot.pickup_hours && <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> Retiro: {lot.pickup_hours}</p>}
              <p className="flex items-center gap-2 font-medium text-[#05130e]" data-testid="lot-pickup-note"><Weight className="w-4 h-4" /> {lot.pickup_note}</p>
              {lot.hazardous && <p className="text-xs font-semibold text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="lot-hazardous-badge">Residuo peligroso: retiro solo por gestor autorizado (SIDREP)</p>}
              <p className="flex items-center gap-2"><Building2 className="w-4 h-4" /> {lot.owner_name}</p>
            </div>
          </div>
          {lot.mode === "subasta" ? <AuctionPanel data={data} reload={load} /> : <FixedPanel lot={lot} />}
          <DealBar lot={lot} />
          <SinaderPanel lot={lot} />
          {certificates.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2" data-testid="lot-certificates">
              <p className="font-semibold text-sm">Certificados de análisis</p>
              {certificates.map((c) => (
                <a key={c.id} href={fileUrl(c.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#00915f] hover:underline"><FileText className="w-4 h-4" /> {c.original_filename}</a>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
